package ui.promo

import io.kvision.core.AlignItems
import io.kvision.core.CssSize
import io.kvision.core.JustifyContent
import io.kvision.core.UNIT
import io.kvision.form.text.textInput
import io.kvision.html.Button
import io.kvision.html.P
import io.kvision.html.button
import io.kvision.html.div
import io.kvision.html.h3
import io.kvision.html.li
import io.kvision.html.p
import io.kvision.html.span
import io.kvision.html.ul
import io.kvision.modal.ModalSize
import io.kvision.panel.SimplePanel
import io.kvision.panel.VPanel
import io.kvision.panel.hPanel
import io.kvision.panel.vPanel
import io.kvision.utils.perc
import io.kvision.utils.px
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.ttt.autogenesis.kvisionapp.ServerExtendBridge
import org.ttt.autogenesis.logging.LogCategory
import org.ttt.autogenesis.logging.Logger
import org.ttt.autogenesis.network.RpcJson
import structs.accelbyte.promo.CreatePromoCodeBatchRequest
import structs.accelbyte.promo.CreatePromoCodeBatchResponse
import structs.accelbyte.promo.DeletePromoCodeRequest
import structs.accelbyte.promo.DeletePromoCodeResponse
import structs.accelbyte.promo.ListPromoCodesRequest
import structs.accelbyte.promo.ListPromoCodesResponse
import structs.accelbyte.promo.PromoOutcomeType
import structs.promo.PromoCodeRegistry
import ui.billing.BillingOverlayWindow

/**
 * Operator-side admin console for the cloud-save-backed promo-code
 * registry. Mirrors the player-facing [PromoCodeOverlay] UX but
 * targets the three admin-gated RPCs:
 *   - `server.extend.listPromoCodes`
 *   - `server.extend.createPromoCodeBatch`
 *   - `server.extend.deletePromoCode`
 *
 * Mounted from [ui.MainMenu] via [ui.MainMenu.openPromoAdmin] (gated
 * by [ExtendConfig.adminRpcEnabled] server-side; if disabled, the
 * RPC throws `PromoCodeAdminDisabledException` which the dispatcher
 * maps to HTTP 404, which the client surfaces as a friendly
 * "operator endpoint unavailable" banner).
 *
 * State machine:
 *   - IDLE: blank form, "Add Codes" + "Refresh" actions
 *   - LOADING: throbber while RPC is in flight
 *   - LIST: registry rendered as `<ul>` of cards, one per code
 *   - ADDING: throbber while mint is in flight
 *   - DELETING: throbber while delete is in flight
 *   - ERROR: error banner with RETRY
 *
 * The console renders every code's state (ACTIVE / REDEEMED, plan,
 * expiry, redeemed-by, batch) so the operator can audit their
 * inventory without touching the Admin Portal.
 */
class PromoCodeAdminConsole : BillingOverlayWindow(
    modalTitle = "PROMO CODE ADMIN",
    modalWidth = 920,
    modalMaxHeight = 640,
    extraClassName = "promo-admin-modal"
)
{
    private enum class State
    {
        IDLE,
        LOADING,
        LIST,
        ADDING,
        DELETING,
        ERROR
    }

    private val root: VPanel = vPanel(spacing = 12)
    private val statusLine: P = p("")
    private val batchInput = textInput {
        placeholder = "BATCH NAME (e.g. welcome-july-2026)"
        maxlength = 64
    }
    private val codesInput = textInput {
        placeholder = "CODES (one per line, or comma-separated)"
        maxlength = 4096
    }
    private val monthsInput = textInput {
        placeholder = "MONTHS (default 1)"
        value = "1"
        maxlength = 3
    }
    private val addButton: Button = button("Add Codes")
    private val refreshButton: Button = button("Refresh")
    private val adminCloseButton: Button = button("Close")
    private val retryButton: Button = button("Retry")
    private val statusBanner: SimplePanel = SimplePanel()
    private val codeList: SimplePanel = SimplePanel()

    private var currentState: State = State.IDLE

    private val adminScope = MainScope()
    private val json = Json { ignoreUnknownKeys = true }

    init
    {
        addCssClass("promo-admin-overlay")
        initContent()
    }

    override fun buildContent(host: VPanel)
    {
        root.width = CssSize(100, UNIT.perc)

        statusLine.addCssClass("promo-admin-status")
        statusLine.content = "Operator-side registry. Admin RPC must be enabled on the server (serverExtend.adminRpcEnabled=true)."
        root.add(statusLine)

        batchInput.addCssClass("promo-admin-input")
        batchInput.width = CssSize(100, UNIT.perc)
        codesInput.addCssClass("promo-admin-input")
        codesInput.width = CssSize(100, UNIT.perc)
        monthsInput.addCssClass("promo-admin-input")
        monthsInput.width = CssSize(8, UNIT.em)

        addButton.addCssClass("btn")
        addButton.addCssClass("btn-promo-admin-add")
        addButton.onClick { triggerAdd() }
        refreshButton.addCssClass("btn")
        refreshButton.addCssClass("btn-promo-admin-refresh")
        refreshButton.onClick { triggerRefresh() }
        adminCloseButton.addCssClass("btn")
        adminCloseButton.onClick { dismiss() }
        retryButton.addCssClass("btn")
        retryButton.onClick { triggerRefresh() }

        statusBanner.addCssClass("promo-admin-banner")
        statusBanner.width = CssSize(100, UNIT.perc)
        statusBanner.hide()
        codeList.addCssClass("promo-admin-list")
        codeList.width = CssSize(100, UNIT.perc)

        val inputRow = vPanel(spacing = 6)
        inputRow.add(batchInput)
        inputRow.add(codesInput)
        val monthsRow = hPanel(spacing = 8, alignItems = AlignItems.CENTER)
        monthsRow.add(monthsInput)
        monthsRow.add(span("month subscription (CREDITS_GRANT not yet wired; default 1mo)"))
        inputRow.add(monthsRow)

        val actionRow = hPanel(spacing = 8, alignItems = AlignItems.CENTER, justify = JustifyContent.FLEXEND)
        actionRow.add(refreshButton)
        actionRow.add(addButton)
        actionRow.add(adminCloseButton)
        actionRow.add(retryButton)

        root.add(inputRow)
        root.add(actionRow)
        root.add(statusBanner)
        root.add(codeList)

        host.add(root)
        renderState(State.IDLE)
        triggerRefresh()
    }

    private fun renderState(state: State)
    {
        currentState = state
        Logger.debug(LogCategory.UI, "PromoCodeAdminConsole.renderState: state=$state")
        when (state)
        {
            State.IDLE -> renderIdle()
            State.LOADING -> renderThrobber("Loading registry…")
            State.LIST -> renderList()
            State.ADDING -> renderThrobber("Adding codes…")
            State.DELETING -> renderThrobber("Deleting code…")
            State.ERROR -> renderError()
        }
    }

    private fun renderIdle()
    {
        addButton.disabled = false
        refreshButton.disabled = false
        retryButton.hide()
        statusBanner.hide()
    }

    private fun renderThrobber(message: String)
    {
        addButton.disabled = true
        refreshButton.disabled = true
        retryButton.hide()
        codeList.removeAll()
        codeList.show()
        codeList.add(renderThrobberRow(message))
    }

    private fun renderList()
    {
        addButton.disabled = false
        refreshButton.disabled = false
        retryButton.hide()
        statusBanner.hide()
        codeList.show()
    }

    private fun renderError()
    {
        addButton.disabled = false
        refreshButton.disabled = false
        retryButton.show()
    }

    private fun renderThrobberRow(message: String): io.kvision.panel.HPanel
    {
        val row = hPanel(spacing = 8, alignItems = AlignItems.CENTER)
        row.addCssClass("promo-admin-throbber")
        val spinner = div("")
        spinner.addCssClass("promo-admin-spinner")
        row.add(spinner)
        val label = span(message)
        label.addCssClass("promo-admin-throbber-label")
        row.add(label)
        return row
    }

    private fun populateList(registry: PromoCodeRegistry)
    {
        codeList.removeAll()
        if(registry.codes.isEmpty())
        {
            val empty = p("No codes defined yet. Add one above.")
            empty.addCssClass("promo-admin-empty")
            codeList.add(empty)
            return
        }
        val summary = p("Total codes: ${registry.codes.size}  •  Active: ${registry.codes.count { it.status == "ACTIVE" }}  •  Redeemed: ${registry.codes.count { it.status == "REDEEMED" }}")
        summary.addCssClass("promo-admin-summary")
        codeList.add(summary)

        val list = ul()
        list.addCssClass("promo-admin-items")
        registry.codes.sortedBy { it.createdAtMillis }.forEach { entry ->
            val item = li()
            item.addCssClass("promo-admin-item")
            item.addCssClass("promo-admin-item-${entry.status.lowercase()}")

            val head = hPanel(justify = JustifyContent.SPACEBETWEEN)
            val code = span(entry.code)
            code.addCssClass("promo-admin-code")
            head.add(code)
            val pill = span(entry.status)
            pill.addCssClass("promo-admin-pill")
            pill.addCssClass(if(entry.status == "ACTIVE") "promo-admin-pill-active" else "promo-admin-pill-redeemed")
            head.add(pill)
            item.add(head)

            val meta = p("Plan: ${entry.plan.name}  •  Days: ${entry.extraSubscriptionDays}  •  Batch: ${entry.batchName.ifBlank { "—" }}  •  Quantity: ${entry.quantity}")
            meta.addCssClass("promo-admin-meta")
            item.add(meta)

            if(entry.redeemedBy != null)
            {
                val stamp = p("Redeemed by ${entry.redeemedBy} at ${entry.redeemedAtMillis}")
                stamp.addCssClass("promo-admin-stamp")
                item.add(stamp)
            }

            val actions = hPanel(spacing = 6)
            val deleteBtn = button("Delete")
            deleteBtn.addCssClass("btn")
            deleteBtn.addCssClass("btn-promo-admin-delete")
            deleteBtn.onClick { triggerDelete(entry.code) }
            actions.add(deleteBtn)
            item.add(actions)

            list.add(item)
        }
        codeList.add(list)
    }

    private fun showError(message: String)
    {
        statusBanner.removeAll()
        statusBanner.show()
        val banner = p(message)
        banner.addCssClass("promo-admin-banner-error")
        statusBanner.add(banner)
        renderState(State.ERROR)
    }

    private fun triggerAdd()
    {
        val codes = parseCodes(codesInput.value.orEmpty())
        if(codes.isEmpty())
        {
            showError("Enter at least one code to add.")
            return
        }
        val months = monthsInput.value?.toIntOrNull()?.coerceAtLeast(1) ?: 1
        val batch = batchInput.value.orEmpty().ifBlank { "batch-${(kotlin.js.Date.now()).toLong() / 1000L}" }
        val request = CreatePromoCodeBatchRequest(
            batchName = batch,
            codes = codes,
            outcomeType = PromoOutcomeType.SUBSCRIPTION_MONTHS,
            subscriptionMonths = months,
        )
        renderState(State.ADDING)
        adminScope.launch {
            try
            {
                ServerExtendBridge.withTemporaryConnection {
                    val invoker = ServerExtendBridge.rpcInvoker
                        ?: error("ServerExtendBridge has no invoker (not connected)")
                    val response = invoker.invoke(
                        "server.extend.createPromoCodeBatch",
                        request,
                        CreatePromoCodeBatchRequest.serializer(),
                    )
                    val error = response.error
                    if(error != null)
                    {
                        throw IllegalStateException("createPromoCodeBatch error: ${error.message}")
                    }
                    val result = response.result
                        ?: throw IllegalStateException("createPromoCodeBatch returned no result")
                    val payload = RpcJson.decodeFromJsonElement(
                        CreatePromoCodeBatchResponse.serializer(),
                        result,
                    )
                    Logger.info(LogCategory.SYSTEM, "PromoAdmin: minted ${payload.codeValues.size} code(s) batch=$batch")
                }
                codesInput.value = ""
                triggerRefresh()
            }
            catch (e: Throwable)
            {
                Logger.error(LogCategory.UI, "PromoAdmin: add failed: ${e.message}")
                showError("Add failed: ${e.message ?: "unknown"}")
            }
        }
    }

    private fun triggerRefresh()
    {
        renderState(State.LOADING)
        adminScope.launch {
            try
            {
                val registry = ServerExtendBridge.withTemporaryConnection {
                    val invoker = ServerExtendBridge.rpcInvoker
                        ?: error("ServerExtendBridge has no invoker (not connected)")
                    val response = invoker.invoke(
                        "server.extend.listPromoCodes",
                        ListPromoCodesRequest(),
                        ListPromoCodesRequest.serializer(),
                    )
                    val error = response.error
                    if(error != null)
                    {
                        throw IllegalStateException("listPromoCodes error: ${error.message}")
                    }
                    val payload = response.result
                        ?: throw IllegalStateException("listPromoCodes returned no result")
                    val listResponse = RpcJson.decodeFromJsonElement(
                        ListPromoCodesResponse.serializer(),
                        payload,
                    )
                    json.decodeFromString(
                        PromoCodeRegistry.serializer(),
                        listResponse.registryJson,
                    )
                }
                populateList(registry)
                renderState(State.LIST)
            }
            catch (e: Throwable)
            {
                Logger.error(LogCategory.UI, "PromoAdmin: refresh failed: ${e.message}")
                showError("Refresh failed: ${e.message ?: "unknown"}")
            }
        }
    }

    private fun triggerDelete(code: String)
    {
        renderState(State.DELETING)
        adminScope.launch {
            try
            {
                ServerExtendBridge.withTemporaryConnection {
                    val invoker = ServerExtendBridge.rpcInvoker
                        ?: error("ServerExtendBridge has no invoker (not connected)")
                    val response = invoker.invoke(
                        "server.extend.deletePromoCode",
                        DeletePromoCodeRequest(codeValue = code),
                        DeletePromoCodeRequest.serializer(),
                    )
                    val error = response.error
                    if(error != null)
                    {
                        throw IllegalStateException("deletePromoCode error: ${error.message}")
                    }
                    response.result?.let {
                        RpcJson.decodeFromJsonElement(DeletePromoCodeResponse.serializer(), it)
                    }
                }
                triggerRefresh()
            }
            catch (e: Throwable)
            {
                Logger.error(LogCategory.UI, "PromoAdmin: delete failed: ${e.message}")
                showError("Delete failed: ${e.message ?: "unknown"}")
            }
        }
    }

    private fun parseCodes(raw: String): List<String>
    {
        return raw.split('\n', ',', ' ', '\t')
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()
    }
}
